import type { ColumnDef } from "../entities/types";
import {
  type AgGridColumnFilter,
  translateAgGridFilterModel,
} from "./ag-grid-filters";
import type { Filter, FilterOp } from "./types";

/** Inclusive calendar dates (YYYY-MM-DD, UTC day boundaries in queries). */
export type ExploreDateRange = {
  since: string;
  until: string;
};

export type ExploreFilterState = {
  /** Inclusive start (YYYY-MM-DD). Omit with `dateUntil` for all time. */
  dateSince?: string;
  /** Inclusive end (YYYY-MM-DD). Omit with `dateSince` for all time. */
  dateUntil?: string;
  includeCancelled: boolean;
  columnFilters: Filter[];
};

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function formatYmdUtc(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toUtcDay(now: Date): Date {
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
}

function lastNDays(n: number, now: Date): ExploreDateRange {
  const end = toUtcDay(now);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (n - 1));
  return { since: formatYmdUtc(start), until: formatYmdUtc(end) };
}

function previousMonth(now: Date): ExploreDateRange {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const start = new Date(Date.UTC(y, m - 1, 1));
  const end = new Date(Date.UTC(y, m, 0));
  return { since: formatYmdUtc(start), until: formatYmdUtc(end) };
}

function yearToDate(now: Date): ExploreDateRange {
  const day = toUtcDay(now);
  return {
    since: formatYmdUtc(new Date(Date.UTC(day.getUTCFullYear(), 0, 1))),
    until: formatYmdUtc(day),
  };
}

// ---------------------------------------------------------------------------
// Preset config — single source of truth for IDs, labels, and range logic.
// Add a preset here; everything else derives from this array.
// ---------------------------------------------------------------------------

type PresetDef = {
  id: string;
  label: string;
  range: (now: Date) => ExploreDateRange | null;
};

export const EXPLORE_DATE_PRESETS = [
  { id: "7", label: "Last 7 days", range: (now: Date) => lastNDays(7, now) },
  { id: "30", label: "Last 30 days", range: (now: Date) => lastNDays(30, now) },
  { id: "prev_month", label: "Previous month", range: previousMonth },
  { id: "90", label: "Last 90 days", range: (now: Date) => lastNDays(90, now) },
  { id: "ytd", label: "Year to date", range: yearToDate },
  { id: "365", label: "Last year", range: (now: Date) => lastNDays(365, now) },
  { id: "all", label: "All time", range: (_now: Date) => null },
] as const satisfies readonly PresetDef[];

export type ExploreDateRangePreset =
  (typeof EXPLORE_DATE_PRESETS)[number]["id"];

// ---------------------------------------------------------------------------
// Preset operations
// ---------------------------------------------------------------------------

function findPreset(id: ExploreDateRangePreset) {
  return EXPLORE_DATE_PRESETS.find((p) => p.id === id);
}

export function explorePresetToDateRange(
  preset: ExploreDateRangePreset,
  now = new Date(),
): ExploreDateRange | null {
  return findPreset(preset)?.range(now) ?? null;
}

export function matchesExploreDatePreset(
  state: ExploreFilterState,
  preset: ExploreDateRangePreset,
  now = new Date(),
): boolean {
  const expected = explorePresetToDateRange(preset, now);
  if (!expected) return !state.dateSince && !state.dateUntil;
  return (
    state.dateSince === expected.since && state.dateUntil === expected.until
  );
}

export function activeExploreDatePreset(
  state: ExploreFilterState,
  now = new Date(),
): ExploreDateRangePreset | null {
  for (const preset of EXPLORE_DATE_PRESETS) {
    if (matchesExploreDatePreset(state, preset.id, now)) return preset.id;
  }
  return null;
}

export function applyExploreDatePreset(
  state: ExploreFilterState,
  preset: ExploreDateRangePreset,
  now = new Date(),
): ExploreFilterState {
  const range = explorePresetToDateRange(preset, now);
  if (!range) return { ...state, dateSince: undefined, dateUntil: undefined };
  return { ...state, dateSince: range.since, dateUntil: range.until };
}

export function formatExploreDateRangeLabel(state: ExploreFilterState): string {
  const active = activeExploreDatePreset(state);
  const preset = active ? findPreset(active) : undefined;
  if (preset) return preset.label;

  const range = exploreDateRangeFromFilterState(state);
  if (!range) return "All time";
  if (range.since === range.until) return range.since;
  return `${range.since} – ${range.until}`;
}

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

export function defaultExploreDateRange(now = new Date()): ExploreDateRange {
  const range = explorePresetToDateRange("30", now);
  if (!range) throw new Error("default explore date range is not all-time");
  return range;
}

export function defaultExploreFilterState(): ExploreFilterState {
  const range = defaultExploreDateRange();
  return {
    dateSince: range.since,
    dateUntil: range.until,
    includeCancelled: false,
    columnFilters: [],
  };
}

export function exploreDateRangeFromFilterState(
  state: ExploreFilterState,
): ExploreDateRange | null {
  if (!state.dateSince || !state.dateUntil) return null;
  return { since: state.dateSince, until: state.dateUntil };
}

export function exploreFilterStateToFilters(
  state: ExploreFilterState,
  defaultDateField: string,
  columnMap: Map<string, ColumnDef>,
): Filter[] {
  const filters: Filter[] = [];

  const range = exploreDateRangeFromFilterState(state);
  if (range) {
    const dateModel = exploreDateRangeFilterModel(
      defaultDateField,
      range.since,
      range.until,
    );
    filters.push(...translateAgGridFilterModel(dateModel, columnMap));
  }

  if (!state.includeCancelled) {
    filters.push({ field: "is_cancelled", op: "eq", value: false });
  }

  filters.push(...state.columnFilters);
  return filters;
}

export function upsertColumnFilter(
  state: ExploreFilterState,
  filter: Filter,
): ExploreFilterState {
  const columnFilters = state.columnFilters.filter(
    (existing) => existing.field !== filter.field,
  );
  if (!isEmptyFilter(filter)) {
    columnFilters.push(filter);
  }
  return { ...state, columnFilters };
}

export function serializeExploreFilterState(state: ExploreFilterState): string {
  return JSON.stringify({
    dateSince: state.dateSince,
    dateUntil: state.dateUntil,
    includeCancelled: state.includeCancelled,
    columnFilters: state.columnFilters,
  });
}

export function exploreDateRangeFilterModel(
  defaultDateField: string,
  since: string,
  until: string,
): Record<string, AgGridColumnFilter> {
  return {
    [defaultDateField]: {
      filterType: "date",
      type: "inRange",
      dateFrom: `${since}T00:00:00.000Z`,
      dateTo: `${until}T00:00:00.000Z`,
    },
  };
}

export function operatorsForColumnType(
  type: ColumnDef["type"],
): readonly FilterOp[] {
  switch (type) {
    case "boolean":
      return ["eq"];
    case "number":
      return ["eq", "gt", "gte", "lt", "lte"];
    case "date":
    case "timestamp":
      return ["gte", "lte", "eq"];
    default:
      return [
        "contains",
        "eq",
        "startsWith",
        "endsWith",
        "isNull",
        "isNotNull",
      ];
  }
}

export function operatorNeedsValue(op: FilterOp): boolean {
  return op !== "isNull" && op !== "isNotNull";
}

function isEmptyFilter(filter: Filter): boolean {
  if (filter.op === "isNull" || filter.op === "isNotNull") return false;
  if (filter.op === "in" || filter.op === "notIn") {
    return !Array.isArray(filter.value) || filter.value.length === 0;
  }
  return filter.value === undefined || filter.value === "";
}
